import React from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  CheckCircle2,
  ShieldCheck,
  Award,
  Users,
  Truck,
  Phone,
  Star,
  MapPin,
} from 'lucide-react';
import { services, stats, testimonials, business, galleryImages } from '../data/siteData';

const HERO_IMG = '/images/project-4.jpg';

export default function Home() {
  return (
    <div>
      {/* HERO */}
      <section className="relative overflow-hidden bg-slate-900 text-white">
        <div className="absolute inset-0">
          <img
            src={HERO_IMG}
            alt="MW Stewart Construction asphalt paving in action"
            className="w-full h-full object-cover opacity-40"
            loading="eager"
            fetchpriority="high"
            decoding="async"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-slate-900 via-slate-900/90 to-slate-900/40" />
          <div className="absolute inset-0 bg-grit opacity-60" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 py-24 lg:py-36">
          <div className="max-w-3xl fade-up">
            <div className="inline-flex items-center gap-2 bg-amber-500/15 text-amber-400 border border-amber-500/30 rounded-full px-4 py-1.5 text-xs font-bold tracking-widest uppercase mb-6">
              <span className="w-2 h-2 rounded-full bg-amber-400 animate-pulse" />
              {business.tagline} · {business.subTagline}
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black leading-[1.05] tracking-tight">
              AB & SK's Trusted{' '}
              <span className="text-amber-400">Asphalt Paving</span> Experts
            </h1>
            <p className="mt-6 text-lg text-slate-300 max-w-2xl leading-relaxed">
              Family-run construction firm specialized in Hot Asphalt Paving, Protective Seal
              Coating, and Hot Rubberized Crack Sealing. Serving AB & SK since 1988.
              From residential driveways to large commercial lots — we deliver durable,
              long-lasting results.
            </p>
            <div className="mt-9 flex flex-wrap gap-4">
              <a
                href={`tel:${business.phoneRaw}`}
                className="inline-flex items-center gap-2 bg-amber-500 hover:bg-amber-400 text-slate-900 font-bold px-7 py-3.5 rounded-md shadow-xl hover:shadow-amber-500/20 transition-all"
              >
                Get a Free Quote <ArrowRight size={18} />
              </a>
              <a
                href={`tel:${business.phoneRaw}`}
                className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 border border-white/20 text-white font-semibold px-7 py-3.5 rounded-md backdrop-blur transition-all"
              >
                <Phone size={16} /> {business.phone}
              </a>
            </div>
            <div className="mt-10 flex flex-wrap gap-6 text-sm text-slate-300">
              {['Licensed & Insured', '35+ Years Experience', 'Family Owned', 'Free Estimates'].map(
                (t) => (
                  <span key={t} className="flex items-center gap-2">
                    <CheckCircle2 size={16} className="text-amber-400" /> {t}
                  </span>
                )
              )}
            </div>
          </div>
        </div>
        <div className="absolute bottom-0 left-0 right-0 h-3 stripe-accent" />
      </section>

      {/* STATS */}
      <section className="bg-amber-500">
        <div className="max-w-7xl mx-auto px-6 py-10 grid grid-cols-2 md:grid-cols-4 gap-6">
          {stats.map((s) => (
            <div key={s.label} className="text-center text-slate-900">
              <div className="text-3xl md:text-4xl font-black">{s.value}</div>
              <div className="text-sm font-semibold uppercase tracking-wider mt-1">{s.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* INTRO / WELCOME */}
      <section className="max-w-7xl mx-auto px-6 py-20 grid lg:grid-cols-2 gap-12 items-center">
        <div>
          <div className="text-sm font-bold tracking-[0.25em] text-amber-600 uppercase mb-4">
            Welcome to {business.name}
          </div>
          <h2 className="text-3xl md:text-4xl font-extrabold text-slate-900 leading-tight">
            Building durable surfaces across AB & SK since 1988
          </h2>
          <div className="w-16 h-1 bg-amber-500 my-6" />
          <p className="text-slate-600 leading-relaxed">
            We are a family-run construction firm specialized in Hot Asphalt Paving, Protective
            Seal Coating, and Hot Rubberized Crack Sealing. For more than three decades, AB &
            SK property owners, developers and municipalities have trusted MW Stewart
            Construction for quality workmanship and honest service.
          </p>
          <p className="mt-4 text-slate-600 leading-relaxed">
            We work on projects of every size—from a single residential driveway to large
            commercial lot resurfacing and municipal road work.
          </p>
          <div className="mt-8 grid grid-cols-2 gap-4">
            {[
              { Icon: Award, label: 'Quality Guaranteed' },
              { Icon: ShieldCheck, label: 'Fully Insured' },
              { Icon: Users, label: 'Family Operated' },
              { Icon: Truck, label: 'Modern Equipment' },
            ].map(({ Icon, label }) => (
              <div
                key={label}
                className="flex items-center gap-3 p-4 rounded-lg border border-slate-200 hover:border-amber-500 hover:shadow-md transition-all"
              >
                <div className="w-10 h-10 rounded-md bg-amber-50 flex items-center justify-center">
                  <Icon className="text-amber-600" size={20} />
                </div>
                <span className="font-semibold text-slate-800 text-sm">{label}</span>
              </div>
            ))}
          </div>
          <Link
            to="/about"
            className="mt-8 inline-flex items-center gap-2 text-amber-600 font-bold hover:gap-3 transition-all"
          >
            Learn More About Us <ArrowRight size={16} />
          </Link>
        </div>

        <div className="relative">
          <div className="img-zoom rounded-xl overflow-hidden shadow-2xl">
            <img
              src="/images/project-1.jpg"
              alt="MW Stewart paving project"
              className="w-full h-[480px] object-cover"
              loading="lazy"
              decoding="async"
            />
          </div>
          <div className="absolute -bottom-6 -left-6 hidden sm:block bg-slate-900 text-white p-6 rounded-xl shadow-2xl border-l-4 border-amber-500 max-w-xs">
            <div className="text-amber-400 text-4xl font-black leading-none">35+</div>
            <div className="mt-1 font-semibold">Years Serving AB & SK</div>
            <div className="text-xs text-slate-400 mt-1">Since {business.established}</div>
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section className="bg-slate-50 py-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <div className="text-sm font-bold tracking-[0.25em] text-amber-600 uppercase mb-3">
              What We Do
            </div>
            <h2 className="text-3xl md:text-4xl font-extrabold text-slate-900">Our Core Services</h2>
            <div className="w-16 h-1 bg-amber-500 mx-auto my-5" />
            <p className="text-slate-600">
              Specialized asphalt construction services delivered by a team trained on cutting-edge
              equipment.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-7">
            {services.slice(0, 6).map((s, i) => (
              <article
                key={s.id}
                className="group bg-white rounded-xl overflow-hidden shadow-sm hover:shadow-2xl border border-slate-200 hover:-translate-y-1 transition-all duration-300"
              >
                <div className="img-zoom h-52">
                  <img src={s.image} alt={s.title} className="w-full h-full object-cover" loading="lazy" decoding="async" />
                </div>
                <div className="p-6">
                  <div className="text-amber-600 font-mono font-bold text-xs mb-2">
                    0{i + 1}
                  </div>
                  <h3 className="text-xl font-bold text-slate-900">{s.title}</h3>
                  <p className="mt-3 text-slate-600 text-sm leading-relaxed">{s.short}</p>
                  <Link
                    to="/services"
                    className="mt-5 inline-flex items-center gap-2 text-sm font-bold text-slate-900 group-hover:text-amber-600 transition-colors"
                  >
                    Read More <ArrowRight size={14} />
                  </Link>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* GALLERY PREVIEW */}
      <section className="max-w-7xl mx-auto px-6 py-20">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-10">
          <div>
            <div className="text-sm font-bold tracking-[0.25em] text-amber-600 uppercase mb-3">
              Our Work
            </div>
            <h2 className="text-3xl md:text-4xl font-extrabold text-slate-900">Recent Projects</h2>
            <div className="w-16 h-1 bg-amber-500 mt-5" />
          </div>
          <Link
            to="/gallery"
            className="inline-flex items-center gap-2 font-bold text-slate-900 hover:text-amber-600"
          >
            View Full Gallery <ArrowRight size={16} />
          </Link>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {galleryImages.slice(0, 8).map((g, i) => (
            <div
              key={i}
              className="img-zoom rounded-lg overflow-hidden aspect-[4/3] shadow-md hover:shadow-xl transition-shadow relative"
            >
              <img src={g.src} alt={g.caption} className="w-full h-full object-cover" loading="lazy" decoding="async" />
              <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 via-transparent to-transparent opacity-0 hover:opacity-100 transition-opacity flex items-end p-4">
                <span className="text-white text-sm font-semibold">{g.caption}</span>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="bg-slate-900 text-white py-20 relative overflow-hidden">
        <div className="absolute inset-0 bg-grit opacity-40" />
        <div className="relative max-w-7xl mx-auto px-6">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <div className="text-sm font-bold tracking-[0.25em] text-amber-400 uppercase mb-3">
              Testimonials
            </div>
            <h2 className="text-3xl md:text-4xl font-extrabold">What Our Clients Say</h2>
            <div className="w-16 h-1 bg-amber-500 mx-auto my-5" />
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {testimonials.map((t) => (
              <div
                key={t.name}
                className="bg-slate-800/60 backdrop-blur border border-slate-700 rounded-xl p-7 hover:border-amber-500 transition-colors"
              >
                <div className="flex gap-1 text-amber-400 mb-4">
                  {Array.from({ length: t.rating }).map((_, i) => (
                    <Star key={i} size={16} fill="currentColor" />
                  ))}
                </div>
                <p className="text-slate-200 leading-relaxed">“{t.text}”</p>
                <div className="mt-5 pt-5 border-t border-slate-700">
                  <div className="font-bold">{t.name}</div>
                  <div className="text-xs text-slate-400 flex items-center gap-1.5 mt-1">
                    <MapPin size={12} /> {t.location}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-6 py-20">
        <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-amber-500 to-amber-400 p-10 md:p-14 shadow-xl">
          <div className="relative grid md:grid-cols-3 gap-8 items-center">
            <div className="md:col-span-2">
              <h3 className="text-3xl md:text-4xl font-black text-slate-900 leading-tight">
                Ready to start your next paving project?
              </h3>
              <p className="mt-3 text-slate-800 max-w-xl">
                Get a free, no-obligation estimate from MW Stewart Construction. Trusted across
                Western Canada since 1988.
              </p>
            </div>
            <div className="flex md:justify-end gap-3 flex-wrap">
              <a
                href={`tel:${business.phoneRaw}`}
                className="bg-slate-900 hover:bg-slate-800 text-white font-bold px-7 py-3.5 rounded-md shadow-md transition-colors"
              >
                Request a Quote
              </a>
              <a
                href={`tel:${business.phoneRaw}`}
                className="bg-white hover:bg-slate-50 text-slate-900 font-bold px-7 py-3.5 rounded-md shadow-md transition-colors"
              >
                {business.phone}
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
